/**
 * NbaCardsRow — Next-Best-Action-Karten (P4/B7, Mock A/3): max. 3 regelbasierte
 * Empfehlungen. Karte 1 (größter Hebel) hervorgehoben. Leere Antwort → nichts.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/v2/ui'
import { ChannelRulesDialog } from '@/components/v2/products/channel-rules/ChannelRulesDialog'
import { useNbaCards } from '@/hooks/useIssuesInsights'
import type { NbaCard } from '@/lib/api/issues-insights'
import { EID } from '@/lib/element-ids.registry'
import { cn } from '@/lib/utils'

function CardShell({ hot, tag, title, children }: { hot?: boolean; tag: string; title: string; children: React.ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-2 rounded-lg border p-4', hot ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10' : 'border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900')}>
      <span className="text-xs font-semibold uppercase text-neutral-400">{tag}</span>
      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{title}</p>
      <div className="mt-auto">{children}</div>
    </div>
  )
}

export function NbaCardsRow() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: cards = [], isLoading } = useNbaCards()
  const [rulesChannelId, setRulesChannelId] = useState<string | null>(null)
  if (isLoading || cards.length === 0) return null

  const render = (card: NbaCard, i: number) => {
    if (card.type === 'top_seller_fix')
      return (
        <CardShell key={card.type} hot={i === 0} tag={t('productsIssues.nba.tagTopSeller')}
          title={t('productsIssues.nba.topSellerTitle', { count: card.product_count, channel: card.channel_name, revenue: card.revenue_30d_sum.toLocaleString('de-DE') })}>
          <Button id={EID['issues-nba-start-run']} data-testid="nba-start-run" size="sm"
            onClick={() => navigate(`/v2/tools/text/bulk?products=${card.product_ids.join(',')}&channel=${card.channel_id}`)}>
            {t('productsIssues.nba.startRun')}
          </Button>
        </CardShell>
      )
    if (card.type === 'rule_gap')
      return (
        <CardShell key={card.type} tag={t('productsIssues.nba.tagRuleGap')} title={t('productsIssues.nba.ruleGapTitle', { channel: card.channel_name })}>
          <Button id={EID['issues-nba-open-rules']} data-testid="nba-open-rules" size="sm" variant="outline" onClick={() => setRulesChannelId(card.channel_id)}>
            {t('productsIssues.nba.setupRules')}
          </Button>
        </CardShell>
      )
    return (
      <CardShell key={card.type} tag={t('productsIssues.nba.tagPending')} title={t('productsIssues.nba.pendingTitle', { count: card.count })}>
        <Button id={EID['issues-nba-open-reviews']} data-testid="nba-open-reviews" size="sm" variant="outline"
          onClick={() => navigate(card.run_id ? `/v2/products/issues/runs/${card.run_id}` : '/v2/products/issues/runs')}>
          {t('productsIssues.nba.review')}
        </Button>
      </CardShell>
    )
  }

  return (
    <section aria-label={t('productsIssues.nba.rowLabel')} className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-testid="issues-nba-row">
      {cards.map(render)}
      <ChannelRulesDialog open={rulesChannelId !== null} onClose={() => setRulesChannelId(null)} initialChannelId={rulesChannelId ?? undefined} />
    </section>
  )
}
